import React, { useEffect, useMemo, useState } from "react";
import {
  ScrollView,
  View,
  StyleSheet,
  Text,
  Image,
  useWindowDimensions,
} from "react-native";
import RankCard from "./RankCard";
import {
  BronzeTrophee,
  FirstBackground,
  GoldTrophee,
  SecondBackground,
  SilverTrophee,
  ThirdBackground,
  UnderTitleLight,
} from "../../../../assets";
import RankCardMobile from "./RankCardMobile";
import {
  getMyTournamentStatistics,
  MyTournamentStatistics,
} from "../../../../services/api";
import { useSelector } from "react-redux";
import { selectIsLogged } from "../../../../store/auth/authReducer";

const emptyBreakdown = {
  "1": 0,
  "2": 0,
  "3": 0,
  total: 0,
};

const ProfileTournaments = () => {
  const { width } = useWindowDimensions();
  const isPortrait = width < 1200;

  const [stats, setStats] = useState<MyTournamentStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [contentWidth, setContentWidth] = useState(0);

  const isLogged = useSelector(selectIsLogged);

  useEffect(() => {
    let mounted = true;

    const loadStats = async () => {
      if (!isLogged) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await getMyTournamentStatistics();
        if (mounted) setStats(result);
      } catch (e) {
        console.error("Failed to load tournament statistics", e);
        if (mounted) setStats(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadStats();

    return () => {
      mounted = false;
    };
  }, [isLogged]);

  const bronze = (stats?.bronze ??
    emptyBreakdown) as MyTournamentStatistics["bronze"];
  const silver = (stats?.silver ??
    emptyBreakdown) as MyTournamentStatistics["silver"];
  const gold = (stats?.gold ??
    emptyBreakdown) as MyTournamentStatistics["gold"];

  const mobileCardWidth = Math.max(260, width * 0.78);

  // const desktopGap = 24;
  // const desktopPadding = 32;

  // const desktopCardWidth = useMemo(() => {
  //   if (isPortrait || !contentWidth) return 300;

  //   const availableWidth = contentWidth - desktopPadding * 2 - desktopGap * 2;

  //   return Math.max(180, Math.min(200, availableWidth / 3));
  // }, [contentWidth, isPortrait]);

  if (loading && !stats) {
    return (
      <View style={styles.loadingWrapper}>
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  if (isPortrait) {
    return (
      <View style={styles.mobileWrapper}>
        <Text
          style={[
            styles.tournamentTitle,
            {
              fontSize: 18,
              marginTop: 0,
            },
          ]}
        >
          TOURNAMENT WINNINGS
        </Text>

        <Image source={UnderTitleLight} style={styles.titleLight} />

        <ScrollView
          contentContainerStyle={[
            styles.container,
            {
              flexDirection: "column",
              gap: 6,
              paddingTop: 8,
              paddingBottom: 0,
              marginBottom: 20,
              paddingHorizontal: 0,
            },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <RankCardMobile
            cardWidth={mobileCardWidth}
            backgroundImage={ThirdBackground}
            badge={BronzeTrophee}
            gold={bronze["1"]}
            silver={bronze["2"]}
            bronze={bronze["3"]}
            total={bronze.total}
            color="#FFD700"
          />
          <RankCardMobile
            cardWidth={mobileCardWidth}
            backgroundImage={SecondBackground}
            badge={SilverTrophee}
            gold={silver["1"]}
            silver={silver["2"]}
            bronze={silver["3"]}
            total={silver.total}
            color="#00F0FF"
          />
          <RankCardMobile
            cardWidth={mobileCardWidth}
            backgroundImage={FirstBackground}
            badge={GoldTrophee}
            gold={gold["1"]}
            silver={gold["2"]}
            bronze={gold["3"]}
            total={gold.total}
            color="#FFF400"
          />
        </ScrollView>
      </View>
    );
  }

  return (
    <View
      style={styles.desktopWrapper}
      onLayout={(e) => {
        setContentWidth(e.nativeEvent.layout.width);
      }}
    >
      <Text
        style={[
          styles.tournamentTitle,
          {
            fontSize: 24,
            marginTop: 20,
          },
        ]}
      >
        TOURNAMENT WINNINGS
      </Text>

      <View
        style={[
          styles.desktopCardsRow,
          // {
          //   paddingHorizontal: desktopPadding,
          //   gap: desktopGap,
          // },
        ]}
      >
        <RankCard
          // cardWidth={desktopCardWidth}
          backgroundImage={ThirdBackground}
          badge={BronzeTrophee}
          gold={bronze["1"]}
          silver={bronze["2"]}
          bronze={bronze["3"]}
          total={bronze.total}
          color="#FFD700"
        />
        <RankCard
          // cardWidth={desktopCardWidth}
          backgroundImage={SecondBackground}
          badge={SilverTrophee}
          gold={silver["1"]}
          silver={silver["2"]}
          bronze={silver["3"]}
          total={silver.total}
          color="#00F0FF"
        />
        <RankCard
          // cardWidth={desktopCardWidth}
          backgroundImage={FirstBackground}
          badge={GoldTrophee}
          gold={gold["1"]}
          silver={gold["2"]}
          bronze={gold["3"]}
          total={gold.total}
          color="#FFF400"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  mobileWrapper: {
    width: "100%",
    alignItems: "center",
  },
  desktopWrapper: {
    width: "100%",
    alignItems: "center",
  },
  desktopCardsRow: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "flex-start",
    paddingTop: 80,
    paddingBottom: 34,
  },
  loadingWrapper: {
    alignItems: "center",
    paddingTop: 20,
  },
  loadingText: {
    color: "#d8d8d8",
  },
  tournamentTitle: {
    fontWeight: "900",
    color: "#d8d8d8",
    textAlign: "center",
    marginVertical: 12,
    textShadowColor: "#000",
    textShadowOffset: { width: 1, height: 2 },
    textShadowRadius: 3,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  titleLight: {
    height: 4,
    width: 300,
  },
  totalLights: {
    width: 280,
    height: 3,
  },
});

export default ProfileTournaments;
